import { regular } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Button } from '@material-ui/core';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { StyledButton } from '../../../../App/Shifamed/Components/forms/presenters/StyledListButton';
import { StyledMenuListIcon } from '../../../../App/Shifamed/Components/forms/presenters/StyledMenuListIcon';
import { StyledMenuListItem } from '../../../../App/Shifamed/Components/forms/presenters/StyledMenuListItem';
import { StyledMenuListItemText } from '../../../../App/Shifamed/Components/forms/presenters/StyledMenuListITemText';
import SM from '../../../../App/Shifamed/Utils/SM/SM';
import { documentRevisionsActions } from '../../../../state/ducks/documentRevisions';
import { documentTypeSelectors } from '../../../../state/ducks/documentRevisions/documentType';
import { DOC_TYPE_GROUP } from '../../../../state/ducks/documentRevisions/documentType/types';
import { DocumentRevision, RevisionChangeType, SmartReferenceType } from '../../../../state/ducks/documentRevisions/types';
import { documentsActions } from '../../../../state/ducks/documents';
import { DocumentProposedId } from '../../../../state/ducks/documents/types';
import { AsyncStatus } from '../../../../state/types';
import FormMessage from '../../../components/forms/FormMessage';
import FormProgress from '../../../components/forms/FormProgress';
import { toastError } from '../../../components/notifications';
import Text from '../../../components/Text';
import { documentVersionPath } from '../../../document.revision/utils/paths';
import useActionCreator from '../../../hooks/useActionCreator';
import useAsync from '../../../hooks/useAsync';

interface Props {
  documentRevision: DocumentRevision
  renderAsButton?: boolean
  label?: string
}

const CreateDeviation: React.FunctionComponent<Props> = ({
  documentRevision,
  renderAsButton,
  label = 'documentRevision.deviation.create',
}) => {
  const isNewLayout = SM.isNewLayout;
  const history = useHistory();

  const allDocTypesById = useSelector(documentTypeSelectors.byId);
  const deviationDocType = useMemo(
    () => Object.values(allDocTypesById).find(dt => dt.group === DOC_TYPE_GROUP.LHR_DEVIATION),
    [allDocTypesById],
  );

  const fetchDevDocId = useActionCreator(documentsActions.loadDocumentProposedIdWithCallback);
  const createDevDoc = useActionCreator(documentRevisionsActions.create);

  const createAsync = useAsync<DocumentRevision>({
    onSuccess: (rev) => {
      history.push(documentVersionPath(rev?.id ?? '', rev?.document?.id ?? ''));
    },
    onError: toastError,
  });

  const fetchIdAsync = useAsync<DocumentProposedId>({
    onSuccess: (data) => {
      const body = {
        formInput: {
          'deviation-selected-lhrt': documentRevision.formDocument?.id,
          'deviation-affected-lhr': [documentRevision.id],
          'deviation-serial-numbers': [
            { [documentRevision.id]: { whereUsed: false, serialNumbers: [] } },
          ],
        },
        document: {
          docId: (data?.docId as string).trim(),
          documentType: { id: deviationDocType?.id },
          securityList: { securityGroups: [], securityEmployees: [], operators: [] },
        },
        revisionChangeType: RevisionChangeType.NextRevision,
      };
      createAsync.start(createDevDoc, body, createAsync);
    },
    onError: toastError,
  });

  const onClick = () => {
    const existing = documentRevision?.smartReferencesFrom?.find(
      ref => ref.active && ref.type === SmartReferenceType.DeviationAffectedLHR,
    );
    if (existing?.fromDocRev?.id && existing?.fromDocRev?.documentId) {
      history.push(documentVersionPath(existing.fromDocRev.id, existing.fromDocRev.documentId));
      return;
    }
    if (!deviationDocType?.id) {
      return;
    }
    fetchIdAsync.start(fetchDevDocId, deviationDocType.id, fetchIdAsync);
  };

  const isLoading = fetchIdAsync.asyncState.status === AsyncStatus.Active
    || createAsync.asyncState.status === AsyncStatus.Active;

  return (
    <>
      <Box position="absolute" marginTop="-50px">
        <FormMessage asyncState={createAsync.asyncState} />
        <FormProgress asyncState={createAsync.asyncState} />
      </Box>
      {isNewLayout ? (
        renderAsButton ? (
          <StyledButton
            color="primary"
            id="create-deviation-button"
            data-cy="create-deviation-button"
            onClick={onClick}
            disabled={isLoading}
          >
            <Text message={label} />
          </StyledButton>
        ) : (
          <StyledMenuListItem
            button
            disabled={isLoading}
            alignItems="flex-start"
            onClick={onClick}
          >
            <StyledMenuListIcon>
              <FontAwesomeIcon icon={regular('file-dashed-line')} size="lg" />
            </StyledMenuListIcon>
            <StyledMenuListItemText
              id="create-deviation-button"
              data-cy="create-deviation-button"
              primary={<Text message={label} />}
            />
          </StyledMenuListItem>
        )
      ) : (
        <Button
          id="create-deviation-button"
          data-cy="create-deviation-button"
          variant="outlined"
          color="secondary"
          onClick={onClick}
          disabled={isLoading}
        >
          <Text message={label} />
        </Button>
      )}
    </>
  );
};

export default CreateDeviation;
